import { defineStore } from 'pinia'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

import { grpcReq } from '@/grpc-client/grpc/grpc-request'
import { grpcCall } from '@/grpc-client/grpc/grpc-call'
import { $grpcClient } from '@/grpc-client/grpc-client'
import { EventLogger } from '@/utils/EventLogger'
import { grpcErrorMessage } from '@/utils/grpcErrorMessage'

import type { IPayStoreState } from '@/modules/pay/types/IPayStoreState'
import type { ICreatePaymentPayload } from '@/modules/pay/types/ICreatePaymentPayload'
import type { StreamUpdatesRequest } from '@vpn-tg/vue-client-grpc/proto/transaction/transaction-endpoint'
import type { ICreateStreamUpdatePayload } from '@/modules/pay/types/ICreateStreamUpdatePayload'
// Enum способов оплаты (ЮKassa / Manual)
import { PaymentMethod } from '@vpn-tg/vue-client-grpc/proto/enums/paymentMethod'
import { useApiStore } from '@/modules/api/store/apiStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import router from '@/router'
import { ERouteNames } from '@/router/ERouteNames'




// Этот стор используется для верификаций промокодов, контролю оплаты, обновления данных связанных с длительностью учетной записи
// Данные отсюда после изменений сливаются в userStore а затем пересылаются на все сервера 3X-Ui для обновления актуальности подписок

export const usePayStore = defineStore('payStore', {
  state: (): IPayStoreState => ({
    selectedTariff: '',
    isTariffActiveSuccessful: false,
    confirmationToken: '',
    paymentId: '',
    paymentError: undefined,
    savedPaymentMethods: [],
    isSavedPaymentMethodsLoaded: false,
  }),
  getters: {
    hasActiveAutoRenew: (state): boolean => {
      return state.savedPaymentMethods.some((pm) => pm.isAutoRenew)
    },
  },
  actions: {
    async createPayment(payload: ICreatePaymentPayload ): Promise<{ ok: boolean; error?: string }> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        // Создаем запрос с автоматическим добавлением accessToken
        const request = grpcReq({
          tariffID: payload.tariffID,
          paymentMethod: payload.paymentMethod,
          savePaymentMethod: payload.savePaymentMethod ?? (payload.paymentMethod === PaymentMethod.YooKassa),
        })

        // gRPC вызов CreatePayment
        const [ok, response] = await grpcCall(
          (_args, options) => {
            return $grpcClient.TransactionEndpoint.createTransaction(request, options)
          },
          'createPayment'
        )

        // Успешный ответ
        if (ok && response && (response.confirmationToken || response.id)) {
          this.confirmationToken = response.confirmationToken || '' // <-- Сохраняем токен
          this.paymentId = response.id // <-- Сохраняем id платежа, если нужно
          return { ok: true }
        }

        // Обработка ошибки из ответа
        if (response && response.error) {
          EventLogger.error('createPayment', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`
          ])
        } else {
          EventLogger.error('createPayment', ['Не удалось создать платеж через gRPC'])
        }
        return {
          ok: false,
          error: grpcErrorMessage(response?.error, 'Не удалось создать платеж'),
        }
      } catch (error: unknown) {
        // Ловим исключения
        console.error('createPayment CATCH:', error)
        EventLogger.error('createPayment', [`Ошибка createPayment: ${(error as Error).message}`])
        return { ok: false, error: (error as Error).message || 'Не удалось создать платеж' }
      } finally {
        // Всегда выключаем глобальный лоадинг
        feedbackStore.isGlobalLoading = false
      }
    },
    async streamPaymentStatus(payload: ICreateStreamUpdatePayload): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      this.isTariffActiveSuccessful = false
      this.paymentError = undefined

      let finalStatus: number | null = null

      try {
        const request: StreamUpdatesRequest = grpcReq({ id: payload.paymentId })
        const stream = $grpcClient.TransactionEndpoint.streamUpdates(request)
        for await (const response of stream.responses) {
          if (response.error) {
            EventLogger.error('createPayment', ['Ошибка streamUpdates:', response.error])
            this.paymentError = response.error
            finalStatus = null
            break
          }
          if (response.status === 3) {
            this.isTariffActiveSuccessful = true
            finalStatus = 3
            break
          }
          if (response.status === 2) {
            finalStatus = response.status
            break
          }
        }
        console.log('streamPaymentStatus завершился со статусом:', finalStatus)
      } catch (error) {
        EventLogger.error('createPayment', [`Ошибка createPayment: ${(error as Error).message}`])
        console.log('streamPaymentStatus завершился с ошибкой')
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },
    /**
     * Запускает отложенный платёж, сохранённый гостем до логина.
     *
     * Читает ID тарифа из localStorage по ключу `vpn_pending_tariff_id`. Если ключ пуст —
     * возвращает false. Если ключ есть — удаляет его (чтобы повторный вход не создавал
     * платёж снова) и вызывает существующий createPayment с PaymentMethod.YooKassa.
     *
     * @returns true, если платёж успешно создан и готов к открытию в PaymentOverlay;
     *          false, если отложенного тарифа нет или createPayment завершился ошибкой.
     */
    async tryStartPendingPayment(): Promise<boolean> {
      // 1. Читаем сохранённый гостем ID тарифа
      const pendingTariffId = localStorage.getItem('vpn_pending_tariff_id')
      if (!pendingTariffId) return false

      // 2. Удаляем сразу, чтобы повторный логин не запускал оплату снова
      localStorage.removeItem('vpn_pending_tariff_id')

      // 3. Гарантируем загрузку тарифов для определения isPromo
      const apiStore = useApiStore()
      if (!apiStore.tariffs.length) {
        await apiStore.getTariffs()
      }
      const targetTariff = apiStore.tariffs.find((t) => t.id === pendingTariffId)
      const isPromo = targetTariff?.isPromo ?? false

      // 4. Для промо-тарифа проверяем привязку Telegram
      if (isPromo) {
        const userStore = useUserStore()
        if (!userStore.user?.telegramID) {
          const feedbackStore = useFeedbackStore()
          feedbackStore.showToast({
            type: 'error',
            message: 'Для активации пробного тарифа необходимо привязать Telegram',
            time: 15000,
          })
          await router.replace({ name: ERouteNames.PROFILE })
          return false
        }
      }

      // 5. Запускаем создание платежа через YooKassa
      const result = await this.createPayment({
        tariffID: pendingTariffId,
        paymentMethod: PaymentMethod.YooKassa,
      })

      if (!result.ok) {
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: result.error || '',
        })
        return false
      }

      return true
    },
    /**
     * Загружает список сохранённых способов оплаты пользователя
     */
    async loadSavedPaymentMethods(showLoader = true): Promise<void> {
      const feedbackStore = useFeedbackStore()
      if (showLoader) {
        feedbackStore.isGlobalLoading = true
      }
      try {
        const request = grpcReq({})
        const [ok, response] = await grpcCall(
          (_args, options) => {
            return $grpcClient.SavedPaymentMethodEndpoint.getSavedPaymentMethods(request, options)
          },
          'getSavedPaymentMethods'
        )
        if (ok && response && response.savedPaymentMethods) {
          this.savedPaymentMethods = response.savedPaymentMethods
        } else {
          this.savedPaymentMethods = []
        }
      } catch (error) {
        EventLogger.error('loadSavedPaymentMethods', ['Ошибка загрузки сохраненных карт', error])
        this.savedPaymentMethods = []
      } finally {
        this.isSavedPaymentMethodsLoaded = true
        if (showLoader) {
          feedbackStore.isGlobalLoading = false
        }
      }
    },
    /**
     * Переключает признак автопродления для сохранённой карты
     */
    async toggleAutoRenew(id: string, isAutoRenew: boolean): Promise<{ ok: boolean; error?: string }> {
      try {
        const request = grpcReq({ id, isAutoRenew })
        const [ok, response] = await grpcCall(
          (_args, options) => {
            return $grpcClient.SavedPaymentMethodEndpoint.updateSavedPaymentMethod(request, options)
          },
          'updateSavedPaymentMethod'
        )
        if (ok) {
          const item = this.savedPaymentMethods.find((m) => m.id === id)
          if (item) {
            item.isAutoRenew = isAutoRenew
          }
          return { ok: true }
        }
        return {
          ok: false,
          error: grpcErrorMessage(response?.error, 'Не удалось обновить автопродление'),
        }
      } catch (error: unknown) {
        EventLogger.error('toggleAutoRenew', ['Ошибка обновления автопродления', error])
        return { ok: false, error: (error as Error).message || 'Не удалось обновить автопродление' }
      }
    },
    /**
     * Отвязывает сохранённый способ оплаты
     */
    async deleteSavedPaymentMethod(id: string): Promise<{ ok: boolean; error?: string }> {
      try {
        const request = grpcReq({ id })
        const [ok, response] = await grpcCall(
          (_args, options) => {
            return $grpcClient.SavedPaymentMethodEndpoint.deleteSavedPaymentMethod(request, options)
          },
          'deleteSavedPaymentMethod'
        )
        if (ok) {
          this.savedPaymentMethods = this.savedPaymentMethods.filter((m) => m.id !== id)
          return { ok: true }
        }
        return {
          ok: false,
          error: grpcErrorMessage(response?.error, 'Не удалось отвязать карту'),
        }
      } catch (error: unknown) {
        EventLogger.error('deleteSavedPaymentMethod', ['Ошибка отвязки карты', error])
        return { ok: false, error: (error as Error).message || 'Не удалось отвязать карту' }
      }
    },
  },
})
